"use strict";
// Copyright 2022 Fergal Daly <fergald@gmail.com>.
Object.defineProperty(exports, "__esModule", { value: true });
exports.TcbSpotlight = exports.Presenter = exports.SpotlightLinguist = void 0;
const dates = require("./dates");
const lingual = require("./lingual");
const event_api = require("./api/event");
const h = require("./html");
const spotlight_api = require("./api/spotlight");
const TEMPLATE = `
<style>
  #spotlight {
    border: solid 1px;
    font-family: k2s,sans-serif;
    display: flex;
  }
  #detailContainer {
    display: flex;
    flex-direction: column;
    align-self: center;
    align-items: center;
  }
  .image {
    width: 100%;
  }
  .title, .link {
    text-align: center;
  }
  .dates {
    padding-top: 1ch;
  }
  .ticket-button {
    background-color: white;
    color: black;
    display: inline-block;
    opacity: 100%;
    border: solid 3px #00A2C3;
    margin: 2px;
  }
  ::slotted(a.link-button) {
    text-decoration: inherit;
    color: inherit;
  }
</style>
<style id="mode-style-sheet"></style>
<style id="style-sheet"></style>
<div id="spotlight">
  <div id=imageContainer></div>
  <div id=detailContainer></div>
</div>
`;
const ROW_STYLE_SHEET = `
#spotlight {
  flex-direction: row;
}
#imageContainer, #detailContainer {
  width: 46%;
  padding: 2%;
}
`;
const COLUMN_STYLE_SHEET = `
#spotlight {
  flex-direction: column;
}
`;
const MESSAGES = lingual.createLanguageMap([lingual.EN, lingual.JA], [['tickets-and-info', 'Tickets & Info', '詳細・チケット']]);
class SpotlightLinguist extends lingual.Linguist {
    constructor(linguist, messages) {
        super(linguist.language, messages);
        this.linguist = linguist;
    }
    static fromLanguage(language) {
        const parentLinguist = lingual.Linguist.fromLanguage(language);
        return new SpotlightLinguist(parentLinguist, MESSAGES.getMessageMapOrThrow(language));
    }
    static fromDocument() {
        return this.fromLanguage(lingual.languageFromDocument(document));
    }
    getMessages() {
        return this.messages;
    }
}
exports.SpotlightLinguist = SpotlightLinguist;
class Presenter {
    constructor(timezone, now, linguist) {
        this.timezone = timezone;
        this.now = now;
        this.linguist = linguist;
    }
    singleSpotlightEventModel(event) {
        return this.commonSpotlightEventModel([event]);
    }
    commonSpotlightEventModel(events) {
        const dates = events.map(e => e.simpleDate);
        return {
            imageUrl: uniqueProperty(events, e => e.spotlightImage, 'spotlightImage'),
            title: uniqueProperty(events, e => e.title, 'title'),
            datesTimes: this.linguist.getWordyDateList(dates),
            //dates: [this.date(event.simpleDate)],
            description: uniqueProperty(events, e => e.spotlightDescription, 'spotlightDescription'),
            url: uniqueProperty(events, e => e.ticketUrl, 'URL'),
        };
    }
}
exports.Presenter = Presenter;
function uniqueProperty(events, get, name) {
    return uniqueItem(events.map(get), name);
}
function uniqueItem(items, name) {
    const array = Array.from(new Set(items));
    if (array.length != 1) {
        console.error(`more than 1 ${name}`, array);
    }
    return array[0] || '';
}
class Model {
    constructor(presenter, events) {
        this.presenter = presenter;
        this.events = events;
    }
    setEvents(events) {
        this.events = events;
    }
    getEvent() {
        if (this.events.length == 1) {
            return this.presenter.singleSpotlightEventModel(this.events[0]);
        }
        else if (this.events.length > 0) {
            return this.presenter.commonSpotlightEventModel(this.events);
        }
    }
}
class TcbSpotlight extends HTMLElement {
    constructor() {
        super();
        this.timezone = new dates.Timezone(9); // Japan time.
        this.linguist = SpotlightLinguist.fromDocument();
        this.presenter = new Presenter(this.timezone, this.timezone.getSimpleDateFromMs(Date.now()), this.linguist);
        this.model = new Model(this.presenter, []);
        this.root = this.attachShadow({ mode: 'open' });
        this.root.innerHTML = TEMPLATE;
        this.updateMode(spotlight_api.Modes.row, '');
    }
    connectedCallback() { }
    static get observedAttributes() {
        return ['data', 'mode', 'style-sheet'];
    }
    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'data') {
            this.updateData(newValue, oldValue);
        }
        else if (name === 'mode') {
            this.updateMode(newValue, oldValue);
        }
        else if (name === 'style-sheet') {
            this.updateStyleSheet(newValue, oldValue);
        }
    }
    updateStyleSheet(newValue, oldValue) {
        const styleSheet = (this.root.getElementById('style-sheet'));
        if (styleSheet) {
            styleSheet.textContent = newValue;
        }
        else {
            console.error('No style sheet');
        }
    }
    updateData(newValue, oldValue) {
        if (oldValue == newValue) {
            return;
        }
        const data = JSON.parse(newValue);
        if (data.events) {
            this.updateEvents(data.events);
        }
    }
    getModeStyleSheet(mode) {
        console.log(mode, spotlight_api.Modes.column);
        switch (mode) {
            case spotlight_api.Modes.column:
                return COLUMN_STYLE_SHEET;
            default:
                return ROW_STYLE_SHEET;
        }
    }
    updateMode(newMode, oldMode) {
        if (newMode == oldMode) {
            return;
        }
        const modeStyle = (this.root.getElementById('mode-style-sheet'));
        if (modeStyle) {
            modeStyle.textContent = this.getModeStyleSheet(newMode);
        }
    }
    updateEvents(eventJsons) {
        const events = event_api.upgradeEvents(this.timezone, eventJsons);
        for (const event of events) {
            if (!event.spotlightImage) {
                console.warn('Spotlight event missing spotlight image', event.url);
            }
        }
        this.model.setEvents(events);
        this.render();
    }
    render() {
        const event = this.model.getEvent();
        if (event) {
            const spotlightDiv = (this.root.getElementById('spotlight'));
            this.renderEvent(spotlightDiv, event);
        }
    }
    renderEvent(parentDiv, event) {
        const imageContainer = (parentDiv.querySelector('#imageContainer'));
        if (!imageContainer) {
            console.error('missing imageContainer');
            return;
        }
        const image = h.element('img', imageContainer, 'image');
        image.src = event.imageUrl;
        const detail = parentDiv.querySelector('#detailContainer');
        if (!detail) {
            console.error('missing detailContainer');
            return;
        }
        const title = h.div(detail, 'title');
        title.textContent = event.title;
        const description = h.div(detail, 'description');
        description.textContent = event.description;
        const space = h.div(detail, 'space');
        space.textContent = ' ';
        const dates = h.div(detail, 'dates');
        for (const datesTimes of event.datesTimes) {
            const datesTimesDiv = h.div(dates, 'datestimes');
            const datesSpan = h.span(datesTimesDiv, 'dates');
            datesSpan.textContent = datesTimes.dates;
            const space = h.span(datesTimesDiv);
            space.textContent = ' ';
            const timesSpan = h.span(datesTimesDiv, 'times');
            timesSpan.textContent = datesTimes.times;
        }
        const ticketButton = h.div(detail, 'ticket-button');
        // Hack to that Google Tag Manager can see the <a> in events.
        const ticketSlot = h.slot(ticketButton);
        const ticketLink = h.a(this, event.url, 'link-button');
        ticketLink.setAttribute('target', '_blank');
        ticketLink.innerText = this.linguist.getMessage('tickets-and-info');
    }
}
exports.TcbSpotlight = TcbSpotlight;
window.customElements.define('tcb-spotlight', TcbSpotlight);
